import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';

import { RESPONSE_ACTIONS, RESPONSE_ACTION_LABELS, editableResponseSteps } from '../../const/response-protocol';
import { ResponseAction, ResponseStep } from '../../models/response-protocol.model';

/**
 * Numbered list of incident response steps, shared by Settings (the default order) and the
 * site form (a site's own order). A site only reorders; Settings ([manageSteps]) can also add the
 * optional steps (Client Contact, Client Notification) and remove them again. The incident report is
 * always a step. With [readonly] it only shows the order.
 *
 * Reorder by dragging a row (native HTML5 drag, no extra dependency) or with the up/down
 * buttons, which are also the keyboard path.
 */
@Component({
  selector: 'app-response-order-editor',
  templateUrl: './response-order-editor.component.html',
  styleUrl: './response-order-editor.component.scss',
})
export class ResponseOrderEditorComponent implements OnChanges {
  @Input() steps: ResponseStep[] = [];
  @Input() readonly = false;
  /** Settings only: lets the steps be added and removed, not just reordered. */
  @Input() manageSteps = false;
  @Output() stepsChange = new EventEmitter<ResponseStep[]>();

  readonly labels = RESPONSE_ACTION_LABELS;
  // The incident popup's accent modifier per action (.sw-incident-action--<accent>).
  readonly accentClass: Record<ResponseAction, string> = {
    speaker: 'speaker',
    emergency: 'emergency',
    client_contact: 'client',
    client_notification: 'notify',
    incident_report: 'report',
  };

  rows: ResponseStep[] = [];

  ngOnChanges(): void {
    this.rows = editableResponseSteps(this.steps);
  }

  /** The steps that exist but are not in the list right now, offered as "+ Add" in Settings. */
  get missingActions(): ResponseAction[] {
    const present = new Set(this.rows.map((r) => r.action));
    return RESPONSE_ACTIONS.filter((a) => !present.has(a));
  }

  addStep(action: ResponseAction): void {
    if (this.readonly || !this.manageSteps || this.rows.some((r) => r.action === action)) {
      return;
    }
    this.rows = [...this.rows, { action, note: '' }];
    this.emit();
  }

  /** Every step but the incident report (it closes the incident, so each order must reach it) can go. */
  canRemove(action: ResponseAction): boolean {
    return this.manageSteps && !this.readonly && action !== 'incident_report';
  }

  removeStep(index: number): void {
    const step = this.rows[index];
    if (!step || !this.canRemove(step.action)) {
      return;
    }
    this.rows = this.rows.filter((_, i) => i !== index);
    this.emit();
  }

  move(index: number, delta: number): void {
    const target = index + delta;
    if (target < 0 || target >= this.rows.length) {
      return;
    }
    const next = [...this.rows];
    [next[index], next[target]] = [next[target], next[index]];
    this.rows = next;
    this.emit();
  }

  // ---- Drag to reorder ----
  // The list reorders live while dragging and emits once on drop, so the parent sees one change.
  dragIndex: number | null = null;
  private dragStartOrder = '';

  onDragStart(index: number, event: DragEvent): void {
    if (this.readonly) {
      return;
    }
    this.dragIndex = index;
    this.dragStartOrder = this.orderKey();
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      // Firefox will not start a drag without data.
      event.dataTransfer.setData('text/plain', this.rows[index].action);
    }
  }

  onDragOver(index: number, event: DragEvent): void {
    if (this.dragIndex === null) {
      return;
    }
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
    if (index === this.dragIndex) {
      return;
    }
    const next = [...this.rows];
    const [moved] = next.splice(this.dragIndex, 1);
    next.splice(index, 0, moved);
    this.rows = next;
    this.dragIndex = index;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.onDragEnd();
  }

  onDragEnd(): void {
    if (this.dragIndex === null) {
      return;
    }
    this.dragIndex = null;
    if (this.orderKey() !== this.dragStartOrder) {
      this.emit();
    }
  }

  private orderKey(): string {
    return this.rows.map((r) => r.action).join('>');
  }

  private emit(): void {
    this.stepsChange.emit(this.rows.map((r) => ({ action: r.action, note: '' })));
  }
}
